import { useRef, useState, type ReactNode } from 'react';
import { MenuItem, MenuList, Popover } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import styles from './detail.module.css';

export interface FieldMenuOption {
  id: string;
  label: string;
  leading?: ReactNode | undefined;
}

export interface FieldMenuProps {
  label: string;
  options: FieldMenuOption[];
  value: string | null;
  /** Text of the empty field („Nicht zugeordnet"); the empty field shows the focus look of Backoffice 05. */
  placeholder: string;
  /** Content of the filled field (avatar + name); defaults to the option's label. */
  display?: ReactNode | undefined;
  disabled?: boolean | undefined;
  onChange: (id: string) => void;
}

/**
 * The dropdown fields of the „Zuordnung" card (Backoffice 05–08, A2 „Formularfeld-Dropdown"): 12 px label, field
 * 9/12 with `--inset-well`; empty = accent border + focus ring and a subtle placeholder. Opens a menu of the options.
 */
export function FieldMenu({
  label,
  options,
  value,
  placeholder,
  display,
  disabled,
  onChange,
}: FieldMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(280);
  const selected = options.find((option) => option.id === value) ?? null;
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel} aria-hidden="true">
        {label}
      </span>
      <button
        ref={anchorRef}
        type="button"
        className={clsx(styles.fieldControl, selected === null && styles.fieldEmpty)}
        aria-label={`${label}: ${selected ? selected.label : placeholder}`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          setMenuWidth(anchorRef.current?.offsetWidth ?? 280);
          setOpen((current) => !current);
        }}
      >
        <span className={styles.fieldValue}>{selected ? (display ?? selected.label) : placeholder}</span>
        <span className={styles.fieldCaret} aria-hidden="true">
          ▾
        </span>
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        placement="bottom-start"
        width={menuWidth}
        variant="menu"
        initialFocus="none"
      >
        <MenuList label={label} density="compact" onClose={() => setOpen(false)}>
          {options.map((option) => (
            <MenuItem
              key={option.id}
              label={option.label}
              icon={option.leading}
              active={option.id === value}
              onSelect={() => {
                setOpen(false);
                if (option.id !== value) onChange(option.id);
              }}
            />
          ))}
        </MenuList>
      </Popover>
    </div>
  );
}
